import { useState } from 'react';

import {
  requestVenue, updateEvent, VENUE_BOOKING_LABEL,
  type ManagedEvent, type DirectoryVenue,
} from '../../lib/organizer';
import { ApiError } from '../../lib/apiClient';
import { VenuePicker } from './VenuePicker';
import './OrganizerPages.css';

/**
 * The venue block on an event's management screen.
 *
 * Voleto holds TWO separate venue concepts on an event and they are easy to
 * confuse, so this section names both:
 *
 *   venue_name / venue_city / venue_state  free text the organizer types. This
 *                                          is what buyers see on the event page.
 *   venue_profile_id + venue_booking_status  a real booked room, and what the
 *                                          venue portal answers.
 *
 * The engine does not copy one into the other, so an event can advertise one
 * room while having booked another. Requesting through here offers to keep them
 * in step, as an explicit checkbox rather than a silent write.
 *
 * State machine (verified in task 37): NULL -> pending -> confirmed | declined,
 * and a DECLINED booking can be re-requested, so this screen keeps the picker
 * available in that case instead of treating it as final. Only 'confirmed'
 * closes the door.
 */

const ERRORS: Record<string, string> = {
  venue_already_confirmed:
    'This event already has a confirmed venue. The venue would need to release it first.',
  not_your_event: 'You can only request venues for your own events.',
  venue_not_found: 'That venue no longer exists.',
};

export function EventVenueSection({
  event, onChanged,
}: {
  event: ManagedEvent;
  onChanged: () => void;
}) {
  const [browsing, setBrowsing] = useState(false);
  const [busyVenue, setBusyVenue] = useState<string | null>(null);
  const [alsoSetDisplay, setAlsoSetDisplay] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const status = event.venueBookingStatus;
  const confirmed = status === 'confirmed';

  async function request(venue: DirectoryVenue) {
    setBusyVenue(venue.venueId);
    setError(null);
    try {
      await requestVenue(venue.venueId, event.id);
      if (alsoSetDisplay) {
        await updateEvent(event.id, {
          venueName: venue.venueName,
          venueCity: venue.city ?? undefined,
          venueState: venue.state ?? undefined,
        });
      }
      setBrowsing(false);
      onChanged();
    } catch (err) {
      // The engine answers { error: '<code>' }, surfaced by apiClient as the message.
      const code = err instanceof ApiError ? err.message : '';
      setError(ERRORS[code] ?? (err instanceof ApiError ? err.message : 'Could not send the request.'));
    } finally {
      setBusyVenue(null);
    }
  }

  const displayVenue = [event.venueName, event.venueCity, event.venueState]
    .filter(Boolean).join(', ');

  return (
    <section className="org-card">
      <header className="org-card__head">
        <h2 className="org-card__title">Venue</h2>
        {status && (
          <span className={`org-status-pill org-venue--${status}`}>
            {VENUE_BOOKING_LABEL[status]}
          </span>
        )}
      </header>

      {error && <p className="org-error" role="alert">{error}</p>}

      <dl className="venue-specs">
        <div>
          <dt>Shown on your event page</dt>
          <dd>{displayVenue || <span className="org-hint">Nothing set</span>}</dd>
        </div>
        <div>
          <dt>Booked room</dt>
          <dd>
            {event.venueProfileId
              ? <span className="mono org-hint">{event.venueProfileId}</span>
              : <span className="org-hint">No venue booked through Voleto</span>}
          </dd>
        </div>
      </dl>

      {!status && (
        <p className="org-hint">
          Booking a room through Voleto lets the venue confirm the date and see door
          numbers on the night. You can also just type a venue name on the event details
          if you have arranged it yourself.
        </p>
      )}

      {status === 'pending' && (
        <p className="org-hint">
          Sent. The venue confirms or declines it, you will see the answer here. Nothing
          is held on their calendar until they confirm.
        </p>
      )}

      {status === 'declined' && (
        <p className="org-hint">
          The venue turned this date down. You can request a different room, or ask the
          same one again if something has changed.
        </p>
      )}

      {confirmed && (
        <p className="org-hint ven-ok">
          The venue has committed the room and the date is held on their calendar. To
          change venue, the current booking has to be released first.
        </p>
      )}

      {!confirmed && (
        <>
          {browsing ? (
            <>
              <label className="org-check">
                <input type="checkbox" checked={alsoSetDisplay}
                  onChange={(e) => setAlsoSetDisplay(e.target.checked)} />
                <span>
                  Also update the venue name and city shown on the event page to match the
                  room you request. Voleto keeps those separate from the booking.
                </span>
              </label>
              <VenuePicker
                eventDate={event.eventDate}
                expectedSize={event.expectedAttendance}
                sizeLabel="expected attendance"
                onSelect={request}
                busyVenueId={busyVenue}
              />
              <div className="org-actions">
                <button type="button" className="button button--ghost"
                  onClick={() => setBrowsing(false)}>
                  Close
                </button>
              </div>
            </>
          ) : (
            <div className="org-actions">
              <button type="button" className="button button--secondary"
                onClick={() => setBrowsing(true)}>
                {status ? 'Request a different venue' : 'Browse venues'}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
